import React from 'react';
import { CheckCircle, AlertTriangle, Clock, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Progress } from '../components/ui/progress';

/**
 * ChecklistProgress — shows checklist item statuses for a client's document onboarding.
 * Props:
 *   checklist: string[] — the required document names from the virtual office
 *   documents: object[] — uploaded documents with ai_validation data
 *   onViewDoc: (doc) => void — callback when user clicks a document
 */
export default function ChecklistProgress({ checklist = [], documents = [], onViewDoc }) {
  const [expanded, setExpanded] = React.useState(true);

  if (!checklist || checklist.length === 0) return null;

  // Match each checklist item to its best uploaded document
  const items = checklist.map((item) => {
    const itemName = typeof item === 'string' ? item : item.name || item.key || '';
    const itemLower = itemName.toLowerCase();

    // Find matching document(s)
    const matchedDocs = documents.filter((doc) => {
      // Check if this doc was validated against this checklist item
      if (doc.validation_template) {
        const templateLower = doc.validation_template.toLowerCase().replace(/_/g, ' ');
        if (itemLower.includes(templateLower) || templateLower.includes(itemLower)) return true;
      }
      // Check ai_validation detected_type
      if (doc.ai_validation?.detected_type) {
        const detected = doc.ai_validation.detected_type.toLowerCase();
        if (itemLower.includes(detected) || detected.includes(itemLower)) return true;
      }
      // Fallback: filename contains item name keywords
      const fn = (doc.original_filename || '').toLowerCase();
      const words = itemLower.split(/[\s/()]+/).filter(w => w.length > 3);
      return words.some(w => fn.includes(w));
    });

    // Best match = most recent with best status
    const bestDoc = matchedDocs.sort((a, b) => {
      const statusOrder = { verified: 0, processed: 1, needs_attention: 2, requires_review: 3, uploaded: 4 };
      return (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
    })[0];

    let status = 'pending';
    if (bestDoc) {
      if (bestDoc.status === 'verified' || bestDoc.status === 'processed') {
        const v = bestDoc.ai_validation;
        if (v && v.matches_expected && !v.is_expired && v.is_readable !== false) {
          status = 'verified';
        } else if (v && v.matches_expected) {
          status = 'attention';
        } else {
          status = 'review';
        }
      } else if (bestDoc.status === 'needs_attention') {
        status = 'attention';
      } else if (bestDoc.status === 'requires_review') {
        status = 'review';
      } else {
        status = 'uploaded';
      }
    }

    return { name: itemName, status, doc: bestDoc };
  });

  const verified = items.filter(i => i.status === 'verified').length;
  const attention = items.filter(i => i.status === 'attention' || i.status === 'review').length;
  const total = items.length;
  const pct = total > 0 ? Math.round((verified / total) * 100) : 0;

  const statusConfig = {
    verified: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Verified' },
    attention: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Needs Attention' },
    review: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', label: 'Needs Review' },
    uploaded: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Processing' },
    pending: { icon: FileText, color: 'text-gray-400', bg: 'bg-gray-50', label: 'Pending' },
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold">Document Checklist</h3>
          <span className="text-xs text-muted-foreground">
            {verified} of {total} verified
            {attention > 0 && ` · ${attention} need${attention === 1 ? 's' : ''} review`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">{pct}%</span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </div>

      <Progress value={pct} className="h-2" />

      {expanded && (
        <div className="space-y-1 pt-1">
          {items.map((item, idx) => {
            const cfg = statusConfig[item.status];
            const Icon = cfg.icon;
            return (
              <div
                key={idx}
                className={`flex items-center justify-between p-2 rounded-md ${cfg.bg} ${item.doc ? 'cursor-pointer hover:opacity-80' : ''}`}
                onClick={() => item.doc && onViewDoc?.(item.doc)}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${cfg.color}`} />
                  <span className="text-sm">{item.name}</span>
                </div>
                <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
